






































































































































































































import {
  defineComponent, ref, Ref, computed, watch,
} from 'vue';

import api from '@/api';
import { CSVColumnType } from '@/types';
import { analyzeCSV } from '@/utils/files';
import store from '@/store';

const defaultKeyField = '_key';
const multinetTypes: readonly CSVColumnType[] = ['label', 'boolean', 'category', 'number', 'date'];

export default defineComponent({
  name: 'TableDialog',
  props: {
    workspace: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    // Stepper control
    const step: Ref<number> = ref(1);
    const dialogWidth = computed(() => {
      if (step.value === 2) {
        return 'unset';
      }
      return '35vw';
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sampleRows: Ref<Array<Record<string, any>>> = ref([]);
    const headers = computed(() => {
      const keys = Object.keys(sampleRows.value[0] || {});
      return keys.map((key) => ({
        text: key,
        value: key,
      }));
    });

    const delimiter = ref('');
    const delimiterOptions = [{ text: ',', value: ',' }, { text: ';', value: ';' }, { text: '|', value: '|' }, { text: 'tab', value: '\t' }];

    const quoteChar = ref('"');
    const quoteCharOptions = ['"', '\''];

    const edgeTable = computed(() => {
      const sample = sampleRows.value[0] || {};
      const hasFrom = Object.prototype.hasOwnProperty.call(sample, '_from');
      const hasTo = Object.prototype.hasOwnProperty.call(sample, '_to');
      return hasFrom && hasTo;
    });

    // Type recommendation
    const columnType: Ref<{[key: string]: CSVColumnType}> = ref({});

    async function runCSVAnalysis(newFile: File, userDelim?: string, userQuote?: string) {
      const analysis = await analyzeCSV(newFile, userDelim, userQuote);
      columnType.value = Array.from(analysis.typeRecs.keys()).reduce((acc, key) => ({ ...acc, [key]: analysis.typeRecs.get(key) }), {});

      sampleRows.value = [...analysis.sampleRows];
      delimiter.value = analysis.delimiter;
    }

    const tableCreationError = ref<string | null>(null);

    // File selection
    const selectedFile = ref<File | null>(null);
    const fileName = ref<string | null>(null);
    watch(selectedFile, async (newFile) => {
      tableCreationError.value = null;

      if (newFile === null) {
        fileName.value = null;
        columnType.value = {};

        return;
      }
      fileName.value = newFile.name.replace('.csv', '');

      await runCSVAnalysis(newFile);
    });

    async function delimiterQuoteChanged() {
      // Re-run the analysis with the user-specified delimiter and quote character
      if (selectedFile.value !== null) {
        await runCSVAnalysis(selectedFile.value, delimiter.value, quoteChar.value);
      }
    }

    // Upload options
    const overwrite = ref(false);
    const keyField = ref<string>(defaultKeyField);
    const restoreKeyField = () => { keyField.value = defaultKeyField; };

    // Upload state
    const uploading = ref(false);
    const uploadProgress = ref<number | null>(null);
    // function handleUploadProgress(evt: { loaded: number; total: number; [key: string]: unknown }) {
    //   uploadProgress.value = (evt.loaded / evt.total) * 100;
    // }

    // Reset component
    function resetAllFields() {
      step.value = 1;
      columnType.value = {};

      selectedFile.value = null;
      fileName.value = null;

      overwrite.value = false;
      restoreKeyField();

      uploading.value = false;
      uploadProgress.value = null;
    }

    // Table creation state
    const tableDialog = ref(false);
    const createDisabled = computed(() => selectedFile.value === null || !fileName.value);
    const loading = ref(false);
    async function createTable() {
      if (selectedFile.value === null || fileName.value === null) {
        return;
      }

      const { workspace } = props;
      uploading.value = true;

      try {
        loading.value = true;
        await api.uploadTable(workspace, fileName.value, {
          data: selectedFile.value,
          edgeTable: edgeTable.value,
          columnTypes: columnType.value,
          delimiter: delimiter.value,
          quoteChar: quoteChar.value,
        });

        tableCreationError.value = null;
        tableDialog.value = false;
        loading.value = false;

        emit('success');
        resetAllFields();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        tableCreationError.value = `${Object.values(err.response.data).flat()[0]}`;
      } finally {
        uploading.value = false;
        uploadProgress.value = null;
      }
    }

    const userCanEdit = computed(() => store.getters.userCanEdit);

    return {
      step,
      dialogWidth,
      headers,
      sampleRows,
      delimiter,
      delimiterOptions,
      quoteChar,
      quoteCharOptions,
      delimiterQuoteChanged,
      columnType,
      multinetTypes,
      selectedFile,
      fileName,
      tableDialog,
      tableCreationError,
      uploading,
      uploadProgress,
      createDisabled,
      loading,
      createTable,
      restoreKeyField,
      keyField,
      overwrite,
      userCanEdit,
    };
  },
});
